import TextField from '@mui/material/TextField';
import { SxProps, Theme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';

import { disableAutofillBg } from 'src/common/components/constants/disableAutofillBg';
import LanguageFlagSwitcher from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import { LanguageKeys } from 'src/common/constants/languages';
import {
  normalizeLocalizedString,
  updateLocalizedStringWithInitialSync,
} from 'src/helpers/localizedStringUtils';

interface LocalizedTextFieldProps {
  label: string;
  value: Record<LanguageKeys, string> | undefined;
  onChange: (value: Record<LanguageKeys, string>) => void;
  editorLang: LanguageKeys;
  onEditorLangChange: (lang: LanguageKeys) => void;
  error?: boolean;
  helperText?: string;
  multiline?: boolean;
  minRows?: number;
  margin?: 'dense' | 'normal' | 'none';
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  syncInitialEditToAllLangs?: boolean;
}

export default function LocalizedTextField({
  label,
  value,
  onChange,
  editorLang,
  onEditorLangChange,
  error,
  helperText,
  multiline,
  minRows,
  margin = 'dense',
  size,
  sx,
  syncInitialEditToAllLangs = true,
}: LocalizedTextFieldProps) {
  const normalized = normalizeLocalizedString(value);
  const committedValue = normalized[editorLang] ?? '';
  const [draftValue, setDraftValue] = useState(committedValue);

  useEffect(() => {
    setDraftValue(committedValue);
  }, [editorLang, committedValue]);

  const commitValue = useCallback(
    (nextValue: string) => {
      if (nextValue === committedValue) {
        return;
      }
      const next = syncInitialEditToAllLangs
        ? updateLocalizedStringWithInitialSync(normalized, editorLang, nextValue)
        : { ...normalized, [editorLang]: nextValue };
      onChange(next);
    },
    [committedValue, editorLang, normalized, onChange, syncInitialEditToAllLangs],
  );

  const handleEditorLangChange = (lang: LanguageKeys) => {
    commitValue(draftValue);
    onEditorLangChange(lang);
  };

  return (
    <TextField
      sx={{ ...disableAutofillBg, minWidth: 0, ...sx }}
      label={label}
      variant="outlined"
      fullWidth
      margin={margin}
      size={size}
      multiline={multiline}
      minRows={minRows}
      value={draftValue}
      onChange={e => setDraftValue(e.target.value)}
      onBlur={() => commitValue(draftValue)}
      error={error}
      helperText={helperText}
      slotProps={{
        input: {
          endAdornment: (
            <LanguageFlagSwitcher value={editorLang} onChange={handleEditorLangChange} />
          ),
        },
      }}
    />
  );
}
